<template>
  <div class="shop-picker">
    <div v-if="loading && !shopsData" :class="$style['loader']">
      <Spinner />
    </div>
    <ul
      v-if="shopsData"
      class="striped clickable">
      <li
        v-for="shop in shopsData"
        :key="shop.title"
        data-test-id="shop-picker-item"
        class="h-margin-right-s h-margin-bottom-s"
        :class="{ selected: shop.name === selectedShop }"
        @click="selectShop(shop.name)">
        {{ shop.title }}
        <div class="right">
          <Btn
            v-if="location && shop.distance && !loading"
            color="link"
            size="small">
            {{ shop.distance }}km
          </Btn>
          <Btn
            shape="round"
            :color="shop.name === selectedShop ? 'cta' :'white'"
            size="small"
            :icon-only="shop.name === selectedShop ? 'System/check-line' : 'System/arrow-right-s-line'" />
        </div>
      </li>
    </ul>
    <p v-if="!shopsData && !loading && showError" class="h-text-center">
      {{ $t('general/api-request-error') }}
    </p>

    <Btn
      shape="pill"
      icon-left="Map/map-pin-2-line"
      :loading="loading"
      class="h-margin-top"
      @click="requestLocation">
      {{ $t('general/what-is-the-nearest-shop') }}
    </Btn>
  </div>
</template>
<script setup>
import distanceBetween from '~/helpers/distance-between'
const { showAlert } = useDialog()
const { $localStorage, $api } = useNuxtApp()

const location = ref(null)
const shopsData = ref(null)
const loading = ref(true)
const showError = ref(false)
const emit = defineEmits(['select', 'loaded'])
const props = defineProps({
  selectedShop: {
    type: [Boolean, String],
    default: null
  },
  shops: {
    type: Object,
    default: null
  }
})

const distanceToShop = shop => {
  if (!shopsData.value || !location.value) { return false }

  const foundShop = shopsData.value.find(item => {
    return shop.name === item.name
  })

  if (!foundShop?.geolocation) { return false }
  const distance = distanceBetween(
    foundShop.geolocation.lat,
    foundShop.geolocation.long,
    location.value.lat,
    location.value.long
  )

  return {
    distance: distance.toFixed(1),
    locationLink: foundShop.locationLink
  }
}

const selectShop = shop => {
  emit('select', shop)
}

const getShopData = async () => {
  if (shopsData.value) { return }
  loading.value = true
  try {
    const data = await $api('shops')
    if (data) {
      shopsData.value = props.shops || data
      loading.value = false
    }
  } catch (e) {
    showError.value = true
    loading.value = false
  }
}

const sortByDistances = () => {
  if (!shopsData.value || !location.value) { return false }
  shopsData.value.forEach(shop => {
    shop.distance = distanceToShop(shop).distance
  })
  shopsData.value.sort((a, b) => a.distance - b.distance)
}

const requestLocation = async () => {
  loading.value = true

  await navigator.geolocation.getCurrentPosition(
    async result => {
      location.value = {
        lat: result.coords.latitude,
        long: result.coords.longitude
      }
      await $localStorage.setItem('location', JSON.stringify(location.value))

      await getShopData()
      sortByDistances()
      loading.value = false
    },
    error => {
      showAlert(error.message)
        .then(() => {
          loading.value = false
        })
        .catch(() => {})
    }
  )
}

onActivated(async () => {
  await getShopData()
  sortByDistances()
})

onMounted(async () => {
  location.value = JSON.parse($localStorage.getItem('location'))
  loading.value = false
  await getShopData()
  sortByDistances()
})

</script>

<style lang="scss" module>
.loader {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}
</style>
